// Company Assets API - React Query hooks for centralized file management
// Issue #118 - Company Assets System
// Phase 53 - Added thumbnail generation for images/PDFs

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import toast from 'react-hot-toast';
import { generateThumbnail, canGenerateThumbnail } from '../thumbnail-generator';
import { getCurrentUserCompanyId } from './utils';

// =====================================================
// Types
// =====================================================

export interface CompanyAsset {
  id: string;
  company_id: string;
  uploaded_by_user_id: string;
  filename: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  thumbnail_url: string | null; // Phase 53: Thumbnail preview URL (200x200 PNG)
  description: string | null;
  tags: string[];
  is_shared: boolean;
  version: number;
  parent_asset_id: string | null;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CompanyAssetFilters {
  search?: string;
  tags?: string[];
  is_shared?: boolean;
  uploaded_by?: string;
  mime_types?: string[]; // Filter by specific mime types (e.g., ['image/jpeg', 'image/png'])
}

export interface UploadAssetParams {
  file: File;
  description?: string;
  tags?: string[];
  is_shared?: boolean;
}

export interface UpdateAssetParams {
  id: string;
  description?: string;
  tags?: string[];
  is_shared?: boolean;
}

// =====================================================
// Query Hooks
// =====================================================

/**
 * Get all company assets with optional filters
 */
export function useCompanyAssets(filters?: CompanyAssetFilters) {
  return useQuery({
    queryKey: ['company-assets', filters],
    queryFn: async () => {
      let query = supabase
        .from('company_assets')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      // Apply search filter (filename or description)
      if (filters?.search) {
        query = query.or(
          `filename.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
        );
      }

      // Apply tags filter
      if (filters?.tags && filters.tags.length > 0) {
        // Use JSONB contains operator for tag filtering
        query = query.contains('tags', filters.tags);
      }

      // Apply is_shared filter
      if (filters?.is_shared !== undefined) {
        query = query.eq('is_shared', filters.is_shared);
      }

      // Apply uploaded_by filter
      if (filters?.uploaded_by) {
        query = query.eq('uploaded_by_user_id', filters.uploaded_by);
      }

      // Apply mime_types filter
      if (filters?.mime_types && filters.mime_types.length > 0) {
        // Use IN operator for multiple mime types
        query = query.in('mime_type', filters.mime_types);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as CompanyAsset[];
    },
  });
}

/**
 * Get a single company asset by ID
 */
export function useCompanyAsset(id: string | undefined) {
  return useQuery({
    queryKey: ['company-assets', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('company_assets')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      return data as CompanyAsset;
    },
    enabled: !!id,
  });
}

/**
 * Get usage count for an asset (how many templates/emails use it)
 */
export function useAssetUsageCount(assetId: string | undefined) {
  return useQuery({
    queryKey: ['company-assets', assetId, 'usage-count'],
    queryFn: async () => {
      if (!assetId) return 0;

      const { data, error } = await supabase.rpc('get_asset_usage_count', {
        p_asset_id: assetId,
      });

      if (error) {
        // Fallback to manual count if function doesn't exist
        console.warn('get_asset_usage_count function not found, using fallback');
        const { data: templates, error: templateError } = await supabase
          .from('email_templates')
          .select('attachment_references')
          .is('deleted_at', null);

        if (templateError) return 0;

        let count = 0;
        templates.forEach((template: { attachment_references?: { type: string; asset_id: string }[] }) => {
          const refs = template.attachment_references || [];
          if (
            refs.some(
              (ref) => ref.type === 'asset' && ref.asset_id === assetId
            )
          ) {
            count++;
          }
        });

        return count;
      }

      return data || 0;
    },
    enabled: !!assetId,
  });
}

// =====================================================
// Mutation Hooks
// =====================================================

/**
 * Upload a new asset to storage and create database record
 * Phase 53: Now generates thumbnails for images and PDFs
 */
export function useUploadAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      description,
      tags = [],
      is_shared = true,
    }: UploadAssetParams) => {
      const companyId = await getCurrentUserCompanyId();
      const assetId = crypto.randomUUID();

      // Sanitize filename for storage (URL-safe), but keep original for display
      const sanitizedFilename = sanitizeFilename(file.name);
      const storagePath = `${companyId}/assets/${assetId}-${sanitizedFilename}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('email-attachments')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Phase 53: Generate and upload thumbnail for supported file types
      let thumbnailUrl: string | null = null;
      const thumbnailPath = `${companyId}/thumbnails/${assetId}-thumb.png`;

      if (canGenerateThumbnail(file.type)) {
        try {
          const thumbnailBlob = await generateThumbnail(file);

          if (thumbnailBlob) {
            // Upload thumbnail to storage
            const { error: thumbnailUploadError } = await supabase.storage
              .from('email-attachments')
              .upload(thumbnailPath, thumbnailBlob, {
                cacheControl: '31536000', // 1 year cache for thumbnails
                contentType: 'image/png',
                upsert: false,
              });

            if (thumbnailUploadError) {
              console.warn('Thumbnail upload failed:', thumbnailUploadError);
              // Continue without thumbnail (graceful degradation)
            } else {
              // Get public URL for thumbnail
              const { data: urlData } = supabase.storage
                .from('email-attachments')
                .getPublicUrl(thumbnailPath);

              thumbnailUrl = urlData.publicUrl;
            }
          }
        } catch (thumbnailError) {
          console.warn('Thumbnail generation failed:', thumbnailError);
          // Continue without thumbnail (graceful degradation)
        }
      }

      // Create database record (store original filename for display)
      const { data: asset, error: insertError } = await supabase
        .from('company_assets')
        .insert({
          id: assetId,
          company_id: companyId,
          uploaded_by_user_id: user.id,
          filename: file.name, // Original filename for display
          file_size: file.size,
          mime_type: file.type,
          storage_path: storagePath, // Sanitized path in storage
          thumbnail_url: thumbnailUrl, // Phase 53: Thumbnail URL (null if unsupported)
          description: description || null,
          tags: tags,
          is_shared: is_shared,
        })
        .select()
        .single();

      if (insertError) {
        // Rollback: Delete uploaded file AND thumbnail (if exists)
        const pathsToRemove = [storagePath];
        if (thumbnailUrl) {
          pathsToRemove.push(thumbnailPath);
        }
        await supabase.storage
          .from('email-attachments')
          .remove(pathsToRemove);
        throw insertError;
      }

      return asset as CompanyAsset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-assets'] });
      toast.success('Asset uploaded successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to upload asset: ${error.message}`);
    },
  });
}

/**
 * Update asset metadata (description, tags, sharing)
 */
export function useUpdateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, description, tags, is_shared }: UpdateAssetParams) => {
      const updates: Record<string, unknown> = {};

      // Only add fields that have actual values (not undefined or empty string)
      if (description !== undefined && description !== null) {
        updates.description = description;
      }
      if (tags !== undefined) {
        updates.tags = tags;
      }
      if (is_shared !== undefined) {
        updates.is_shared = is_shared;
      }

      const { data, error } = await supabase
        .from('company_assets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as CompanyAsset;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['company-assets'] });
      queryClient.invalidateQueries({ queryKey: ['company-assets', variables.id] });
      toast.success('Asset updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update asset: ${error.message}`);
    },
  });
}

/**
 * Delete (soft delete) an asset
 */
export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Check usage count first
      const { data: usageCount, error: usageError } = await supabase.rpc(
        'get_asset_usage_count',
        { p_asset_id: id }
      );

      if (usageError) {
        console.warn('Could not check usage count:', usageError);
      } else if (usageCount > 0) {
        throw new Error(
          `This asset is used in ${usageCount} template(s). Remove it from templates before deleting.`
        );
      }

      // Soft delete
      const { error } = await supabase
        .from('company_assets')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      // Note: We don't delete the file from storage to preserve data integrity
      // Physical file deletion can be done via a background job for assets deleted > 30 days
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-assets'] });
      toast.success('Asset deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete asset: ${error.message}`);
    },
  });
}

/**
 * Download asset file
 */
export function useDownloadAsset() {
  return useMutation({
    mutationFn: async (asset: CompanyAsset) => {
      const { data, error } = await supabase.storage
        .from('email-attachments')
        .download(asset.storage_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = asset.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return true;
    },
    onError: (error: Error) => {
      toast.error(`Failed to download asset: ${error.message}`);
    },
  });
}

// =====================================================
// Helper Functions
// =====================================================

/**
 * Sanitize filename to be URL-safe for Supabase Storage
 * Handles international characters (German, French, Spanish, etc.)
 *
 * @example
 * sanitizeFilename('BOLDiX-Händlerpreisliste_2025-09-28.pdf')
 * // Returns: 'BOLDiX-Haendlerpreisliste_2025-09-28.pdf'
 */
export function sanitizeFilename(filename: string): string {
  // Split filename and extension
  const lastDotIndex = filename.lastIndexOf('.');
  const name = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';

  // Transliteration map for common international characters
  const transliterationMap: { [key: string]: string } = {
    // German
    'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss',
    'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue',

    // French
    'à': 'a', 'â': 'a', 'æ': 'ae', 'ç': 'c', 'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
    'î': 'i', 'ï': 'i', 'ô': 'o', 'œ': 'oe', 'ù': 'u', 'û': 'u', 'ü': 'u', 'ÿ': 'y',
    'À': 'A', 'Â': 'A', 'Æ': 'AE', 'Ç': 'C', 'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
    'Î': 'I', 'Ï': 'I', 'Ô': 'O', 'Œ': 'OE', 'Ù': 'U', 'Û': 'U', 'Ü': 'U', 'Ÿ': 'Y',

    // Spanish
    'á': 'a', 'í': 'i', 'ó': 'o', 'ú': 'u', 'ñ': 'n',
    'Á': 'A', 'Í': 'I', 'Ó': 'O', 'Ú': 'U', 'Ñ': 'N',

    // Other common characters
    'å': 'a', 'Å': 'A', 'ø': 'o', 'Ø': 'O', 'đ': 'd', 'Đ': 'D',
    'ł': 'l', 'Ł': 'L', 'ś': 's', 'Ś': 'S', 'ź': 'z', 'Ź': 'Z',
    'ż': 'z', 'Ż': 'Z', 'ć': 'c', 'Ć': 'C', 'ń': 'n', 'Ń': 'N',
  };

  // Apply transliteration
  let sanitized = name.split('').map(char => transliterationMap[char] || char).join('');

  // Replace spaces with hyphens
  sanitized = sanitized.replace(/\s+/g, '-');

  // Remove or replace special characters (keep only: a-z, A-Z, 0-9, -, _)
  sanitized = sanitized.replace(/[^a-zA-Z0-9\-_]/g, '-');

  // Remove consecutive hyphens/underscores
  sanitized = sanitized.replace(/[-_]{2,}/g, '-');

  // Remove leading/trailing hyphens/underscores
  sanitized = sanitized.replace(/^[-_]+|[-_]+$/g, '');

  // If sanitization resulted in empty string, use a fallback
  if (!sanitized) {
    sanitized = 'file';
  }

  return sanitized + extension;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get file icon based on mime type
 */
export function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('video/')) return '🎥';
  if (mimeType.startsWith('audio/')) return '🎵';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive')) return '📦';
  return '📎';
}

/**
 * Validate file for upload
 */
export function validateAssetFile(file: File): string | null {
  const maxSize = 25 * 1024 * 1024; // 25MB
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'text/plain',
    'text/csv',
  ];

  if (file.size > maxSize) {
    return `File size exceeds 25MB limit (${formatFileSize(file.size)})`;
  }

  if (!allowedTypes.includes(file.type)) {
    return `File type not allowed: ${file.type}`;
  }

  return null;
}

/**
 * Helper function to get only image assets
 */
export function useImageAssets(filters?: Omit<CompanyAssetFilters, 'mime_types'>) {
  return useCompanyAssets({
    ...filters,
    mime_types: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  });
}

/**
 * Check if a mime type is an image
 */
export function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Get all unique tags from assets
 */
export function useAssetTags() {
  return useQuery({
    queryKey: ['company-assets', 'tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_assets')
        .select('tags')
        .is('deleted_at', null);

      if (error) throw error;

      // Flatten and deduplicate tags
      const allTags = new Set<string>();
      data?.forEach((asset: { tags: string[] }) => {
        asset.tags?.forEach((tag) => allTags.add(tag));
      });

      return Array.from(allTags).sort();
    },
  });
}

/**
 * Get all users who have uploaded assets
 */
export function useAssetUploaders() {
  return useQuery({
    queryKey: ['company-assets', 'uploaders'],
    queryFn: async () => {
      // Get unique uploader IDs
      const { data: assets, error: assetsError } = await supabase
        .from('company_assets')
        .select('uploaded_by_user_id')
        .is('deleted_at', null);

      if (assetsError) throw assetsError;

      const uploaderIds = [...new Set(assets?.map((a) => a.uploaded_by_user_id))];

      if (uploaderIds.length === 0) return [];

      // Fetch user profiles
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', uploaderIds)
        .is('deleted_at', null);

      if (usersError) throw usersError;

      return users || [];
    },
  });
}

// =====================================================
// Brand-Asset Relationship Hooks
// =====================================================

/**
 * Represents a brand linked to an asset
 */
export interface AssetBrandLink {
  id: string; // brand_assets junction table ID
  brand_id: string;
  brand_name: string;
  asset_type: string | null;
}

/**
 * Get all brands linked to a specific asset
 */
export function useAssetBrands(assetId: string | undefined) {
  return useQuery({
    queryKey: ['asset-brands', assetId],
    queryFn: async (): Promise<AssetBrandLink[]> => {
      if (!assetId) return [];

      const { data, error } = await supabase
        .from('brand_assets')
        .select(`
          id,
          brand_id,
          asset_type,
          brands!inner (
            id,
            name
          )
        `)
        .eq('asset_id', assetId)
        .is('deleted_at', null);

      if (error) throw error;

      return (data || []).map((item: {
        id: string;
        brand_id: string;
        brands: { name: string };
        asset_type: string
      }) => ({
        id: item.id,
        brand_id: item.brand_id,
        brand_name: item.brands.name,
        asset_type: item.asset_type,
      }));
    },
    enabled: !!assetId,
  });
}

/**
 * Extended filters for company assets including brand filtering
 */
export interface CompanyAssetFiltersExtended extends CompanyAssetFilters {
  brand_id?: string | 'unlinked';
  date_from?: string; // ISO date string for created_at >= date_from
  date_to?: string;   // ISO date string for created_at <= date_to
  page?: number;      // Page number for pagination (1-indexed)
  pageSize?: number;  // Number of items per page
}

/**
 * Get company assets with brand filtering support
 * - brand_id = specific UUID: assets linked to that brand
 * - brand_id = 'unlinked': assets with no brand links
 * - brand_id = undefined: all assets (no brand filter)
 *
 * Returns { data: CompanyAsset[], total: number } for pagination support
 */
export function useCompanyAssetsWithBrandFilter(
  filters?: CompanyAssetFiltersExtended,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['company-assets-with-brands', filters],
    enabled,
    queryFn: async () => {
      const brandFilter = filters?.brand_id;
      const page = filters?.page || 1;
      const pageSize = filters?.pageSize || 30;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Helper function to apply common filters to a query
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase query builder types are complex
      const applyFilters = (query: any) => {
        if (filters?.search) {
          query = query.or(
            `filename.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
          );
        }
        if (filters?.is_shared !== undefined) {
          query = query.eq('is_shared', filters.is_shared);
        }
        if (filters?.mime_types && filters.mime_types.length > 0) {
          query = query.in('mime_type', filters.mime_types);
        }
        if (filters?.tags && filters.tags.length > 0) {
          query = query.contains('tags', filters.tags);
        }
        if (filters?.uploaded_by) {
          query = query.eq('uploaded_by_user_id', filters.uploaded_by);
        }
        if (filters?.date_from) {
          query = query.gte('created_at', filters.date_from);
        }
        if (filters?.date_to) {
          // Add 1 day to include the entire end date
          const endDate = new Date(filters.date_to);
          endDate.setDate(endDate.getDate() + 1);
          query = query.lt('created_at', endDate.toISOString());
        }
        return query;
      };

      // If filtering by a specific brand, get asset IDs first
      if (brandFilter && brandFilter !== 'unlinked') {
        // Get asset IDs linked to this brand
        const { data: brandAssets, error: brandError } = await supabase
          .from('brand_assets')
          .select('asset_id')
          .eq('brand_id', brandFilter)
          .is('deleted_at', null);

        if (brandError) throw brandError;

        const linkedAssetIds = (brandAssets || []).map((ba) => ba.asset_id);

        if (linkedAssetIds.length === 0) {
          return { data: [], total: 0 };
        }

        // Get total count with filters
        let countQuery = supabase
          .from('company_assets')
          .select('*', { count: 'exact', head: true })
          .in('id', linkedAssetIds)
          .is('deleted_at', null);

        countQuery = applyFilters(countQuery);
        const { count, error: countError } = await countQuery;
        if (countError) throw countError;

        // Fetch paginated data
        let dataQuery = supabase
          .from('company_assets')
          .select('*')
          .in('id', linkedAssetIds)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .range(from, to);

        dataQuery = applyFilters(dataQuery);
        const { data, error } = await dataQuery;
        if (error) throw error;

        return { data: data as CompanyAsset[], total: count || 0 };
      }

      // If filtering for unlinked assets (client-side filtering required)
      if (brandFilter === 'unlinked') {
        // Get all asset IDs that have brand links
        const { data: linkedAssets, error: linkedError } = await supabase
          .from('brand_assets')
          .select('asset_id')
          .is('deleted_at', null);

        if (linkedError) throw linkedError;

        const linkedAssetIds = [...new Set((linkedAssets || []).map((ba) => ba.asset_id))];

        // Fetch all assets with filters (we need all to filter out linked ones)
        let query = supabase
          .from('company_assets')
          .select('*')
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        query = applyFilters(query);
        const { data, error } = await query;
        if (error) throw error;

        // Filter out assets that have brand links
        const unlinkedAssets = (data || []).filter(
          (asset) => !linkedAssetIds.includes(asset.id)
        );

        // Apply client-side pagination to unlinked assets
        const total = unlinkedAssets.length;
        const paginatedAssets = unlinkedAssets.slice(from, from + pageSize);

        return { data: paginatedAssets as CompanyAsset[], total };
      }

      // No brand filter - use standard query with pagination
      // Get total count
      let countQuery = supabase
        .from('company_assets')
        .select('*', { count: 'exact', head: true })
        .is('deleted_at', null);

      countQuery = applyFilters(countQuery);
      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      // Fetch paginated data
      let dataQuery = supabase
        .from('company_assets')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(from, to);

      dataQuery = applyFilters(dataQuery);
      const { data, error } = await dataQuery;
      if (error) throw error;

      return { data: data as CompanyAsset[], total: count || 0 };
    },
  });
}
